import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import AdminClients from './AdminClients';
import { AdminClientDetailInner } from './AdminClientDetail';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { X, Users, AlertTriangle } from 'lucide-react';

const STORAGE_KEY = 'client_workspace_tabs';
const ACTIVE_KEY = 'client_workspace_active';

function loadSavedTabs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw).map(t => ({ ...t, isDirty: false }));
  } catch {}
  return [];
}

function loadSavedActive() {
  return localStorage.getItem(ACTIVE_KEY) || 'list';
}

export default function AdminClientsWorkspace() {
  const { clientId: urlClientId } = useParams();

  // tabs: array of { id, clientName, isDirty }
  const [tabs, setTabs] = useState(loadSavedTabs);
  const [activeTabId, setActiveTabId] = useState(loadSavedActive);
  const [closeConfirmId, setCloseConfirmId] = useState(null);
  const dirtyMap = useRef({});

  // Open a client tab (or switch to it if already open)
  const openTab = useCallback((clientId, clientName) => {
    setTabs(prev => {
      if (prev.find(t => t.id === clientId)) {
        return prev;
      }
      return [...prev, { id: clientId, clientName: clientName || 'Chargement...', isDirty: false }];
    });
    setActiveTabId(clientId);
  }, []);

  // Request close: check dirty state
  const requestClose = useCallback((tabId, e) => {
    if (e) e.stopPropagation();
    if (dirtyMap.current[tabId]) {
      setCloseConfirmId(tabId);
    } else {
      doClose(tabId);
    }
  }, []);

  // Actually close a tab
  const doClose = useCallback((tabId) => {
    delete dirtyMap.current[tabId];
    setTabs(prev => prev.filter(t => t.id !== tabId));
    setActiveTabId(prev => prev === tabId ? 'list' : prev);
    setCloseConfirmId(null);
  }, []);

  // Dirty tracking
  const markDirty = useCallback((tabId, dirty) => {
    dirtyMap.current[tabId] = dirty;
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, isDirty: dirty } : t));
  }, []);

  // Update tab name when client data loads
  const updateTabName = useCallback((tabId, name) => {
    setTabs(prev => prev.map(t => t.id === tabId ? { ...t, clientName: name } : t));
  }, []);

  // Persist tabs & active tab to localStorage
  useEffect(() => {
    const toSave = tabs.map(({ id, clientName }) => ({ id, clientName }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  }, [tabs]);

  useEffect(() => {
    localStorage.setItem(ACTIVE_KEY, activeTabId);
  }, [activeTabId]);

  // Auto-open from URL (direct link /admin/clients/:clientId)
  useEffect(() => {
    if (urlClientId) {
      openTab(urlClientId, 'Chargement...');
    }
  }, [urlClientId, openTab]);

  return (
    <DashboardLayout>
      {/* Tab bar */}
      <div className="flex items-center border-b border-border mb-4 overflow-x-auto scrollbar-thin" data-testid="client-tabs-bar">
        {/* List tab (always first) */}
        <button
          onClick={() => setActiveTabId('list')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
            activeTabId === 'list'
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
          }`}
          data-testid="tab-clients-list"
        >
          <Users className="w-4 h-4" />
          Clients
        </button>

        {/* Client tabs */}
        {tabs.map(tab => (
          <div
            key={tab.id}
            role="tab"
            tabIndex={0}
            onClick={() => setActiveTabId(tab.id)}
            onKeyDown={(e) => e.key === 'Enter' && setActiveTabId(tab.id)}
            className={`group flex items-center gap-1.5 pl-3 pr-1.5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 max-w-[200px] cursor-pointer ${
              activeTabId === tab.id
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
            data-testid={`tab-client-${tab.id}`}
          >
            {tab.isDirty && (
              <span className="w-2 h-2 rounded-full bg-orange-400 shrink-0" title="Modifications non sauvegardées" />
            )}
            <span className="truncate">{tab.clientName}</span>
            <button
              type="button"
              aria-label={`Fermer l'onglet ${tab.clientName}`}
              onClick={(e) => { e.stopPropagation(); requestClose(tab.id, e); }}
              className="ml-0.5 p-1 rounded-sm opacity-60 hover:opacity-100 hover:bg-destructive/20 hover:text-destructive transition-all shrink-0"
              data-testid={`tab-close-${tab.id}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* Tab content: list */}
      <div style={{ display: activeTabId === 'list' ? 'block' : 'none' }}>
        <AdminClients inWorkspace onClientClick={openTab} />
      </div>

      {/* Tab content: each open client */}
      {tabs.map(tab => (
        <div key={tab.id} style={{ display: activeTabId === tab.id ? 'block' : 'none' }}>
          <AdminClientDetailInner
            clientIdProp={tab.id}
            inWorkspace
            onDirtyChange={(dirty) => markDirty(tab.id, dirty)}
            onClientNameResolved={(name) => updateTabName(tab.id, name)}
            onBack={() => setActiveTabId('list')}
          />
        </div>
      ))}

      {/* Close confirmation dialog */}
      <Dialog open={!!closeConfirmId} onOpenChange={() => setCloseConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Modifications non sauvegardées
            </DialogTitle>
            <DialogDescription>
              Cet onglet contient des modifications non sauvegardées. Voulez-vous vraiment le fermer ?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCloseConfirmId(null)} data-testid="cancel-close-tab">
              Annuler
            </Button>
            <Button variant="destructive" onClick={() => doClose(closeConfirmId)} data-testid="confirm-close-tab">
              Fermer sans sauvegarder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
